import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { Check, X as XIcon, BadgeCheck, Trash2, Receipt, Plus, Upload, Download, Eye, ExternalLink, File as FileIcon } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';
import {
  PAGE_WIDE, DataTable, DetailDrawer, Dialog, Field, FormRow, SearchInput, Tabs, OverflowMenu, BulkBar, StatusPill,
  useUrlTab, money, fmtDate, num, plural,
} from '../../components/ui';
import { ReasonDialog, useStableCallback, toDay } from '../Staff/hrKit';

const CATEGORIES = ['Travel', 'Meals', 'Supplies', 'Equipment', 'Training', 'Phone/Internet', 'Parking/Tolls', 'Accommodation', 'Other'];
const STATUS_VIEW = {
  pending: { label: 'Pending', tone: 'warning' },
  approved: { label: 'Approved, not paid', tone: 'info' },
  reimbursed: { label: 'Reimbursed', tone: 'success' },
  rejected: { label: 'Rejected', tone: 'danger' },
};
const ClaimStatus = ({ status }) => { const v = STATUS_VIEW[status] || { label: status, tone: 'neutral' }; return <StatusPill status={status} label={v.label} tone={v.tone} />; };

// Sums are added in whole cents so a column of claims totals exactly.
const cents = (v) => Math.round((Number(v) || 0) * 100);
const sumMoney = (list) => list.reduce((s, c) => s + cents(c.amount), 0) / 100;
const receiptHref = (url) => url || null;   // /uploads is authorised by the session cookie
const isImage = (url) => /\.(png|jpe?g|gif|webp|bmp)$/i.test(String(url || ''));
const isPdf = (url) => /\.pdf$/i.test(String(url || ''));
const todayKey = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const dateText = (v) => { const d = toDay(v); return d ? fmtDate(d) : '—'; };

/* ── New claim ────────────────────────────────────────────────────────── */

function NewClaimDialog({ open, staff, onClose, onSaved }) {
  const { user, isAdmin, isOffice } = useAuth();
  const isStaffPicker = isAdmin || isOffice;
  const [form, setForm] = useState({});
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [over, setOver] = useState(false);
  const fileRef = useRef();
  useEffect(() => {
    if (!open) return;
    setErrors({}); setFile(null);
    setForm({ staffId: '', category: 'Travel', amount: '', expenseDate: todayKey(), description: '' });
  }, [open]);
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined })); };
  const close = useStableCallback(() => { if (!saving) onClose(); });
  const people = useMemo(() => {
    const list = [...staff];
    if (user && !list.some((s) => s.id === user.id)) list.unshift({ id: user.id, first_name: user.first_name, last_name: user.last_name });
    return list;
  }, [staff, user]);

  const save = async (e) => {
    e.preventDefault();
    const errs = {};
    if (isStaffPicker && !form.staffId) errs.staffId = 'Choose who the claim is for.';
    if (!form.amount || !(parseFloat(form.amount) > 0)) errs.amount = 'Enter an amount above $0.';
    if (!form.expenseDate) errs.expenseDate = 'Enter the date of the expense.';
    if (file && file.size > 15 * 1024 * 1024) errs.file = 'The receipt is larger than 15 MB.';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('amount', form.amount);
      fd.append('category', form.category);
      fd.append('expenseDate', form.expenseDate);
      fd.append('description', form.description);
      if (isStaffPicker && form.staffId) fd.append('staffId', form.staffId);
      if (file) fd.append('receipt', file);
      await api.post('/reimbursements', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Claim submitted');
      setSaving(false);
      onSaved();
      onClose();
    } catch (err) {
      setErrors({ form: err.response?.data?.message || "The claim wasn't submitted. Try again." });
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} size="md" title="New expense claim"
      footer={(
        <>
          {errors.form && <span className="mr-auto text-[13px] text-rose-700" role="alert">{errors.form}</span>}
          <button type="button" onClick={close} className="btn-secondary" disabled={saving}>Cancel</button>
          <button type="submit" form="claim-new" className="btn-primary" disabled={saving}>{saving ? 'Submitting…' : 'Submit claim'}</button>
        </>
      )}>
      <form id="claim-new" onSubmit={save} className="space-y-4">
        {isStaffPicker && (
          <Field label="Claim for" required error={errors.staffId} hint="The staff member who paid, and who will be reimbursed.">
            {(id) => (
              <select id={id} className="input-field" value={form.staffId || ''} onChange={(e) => set('staffId', e.target.value)}>
                <option value="">Choose…</option>
                {people.map((s) => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}{user && s.id === user.id ? ' (you)' : ''}</option>)}
              </select>
            )}
          </Field>
        )}
        <FormRow>
          <Field label="Amount" required error={errors.amount}>
            {(id) => (
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">$</span>
                <input id={id} type="number" min="0" step="0.01" inputMode="decimal" className="input-field pl-7 tabular-nums" value={form.amount || ''} onChange={(e) => set('amount', e.target.value)} />
              </div>
            )}
          </Field>
          <Field label="Date" required error={errors.expenseDate}>
            {(id) => <input id={id} type="date" className="input-field" value={form.expenseDate || ''} onChange={(e) => set('expenseDate', e.target.value)} />}
          </Field>
        </FormRow>
        <Field label="Category">
          {(id) => (
            <select id={id} className="input-field" value={form.category || 'Travel'} onChange={(e) => set('category', e.target.value)}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
        </Field>
        <Field label="Description">
          {(id) => <textarea id={id} className="input-field resize-y" rows={2} value={form.description || ''} onChange={(e) => set('description', e.target.value)} placeholder="What was this expense for?" />}
        </Field>
        <Field label="Receipt" error={errors.file} hint="A photo or PDF, up to 15 MB.">
          {(id) => (
            <div
              onDragOver={(e) => { e.preventDefault(); setOver(true); }}
              onDragLeave={() => setOver(false)}
              onDrop={(e) => { e.preventDefault(); setOver(false); const f = e.dataTransfer.files?.[0]; if (f) { setFile(f); if (errors.file) setErrors((x) => ({ ...x, file: undefined })); } }}
              className={clsx('flex flex-wrap items-center gap-3 rounded-lg border border-dashed px-3 py-3 transition-colors duration-150', over ? 'border-primary-400 bg-primary-50' : 'border-slate-300 bg-slate-50/60')}>
              <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); e.target.value = ''; }} />
              {file ? (
                <span className="inline-flex min-w-0 flex-1 items-center gap-1.5 text-[13px] text-slate-700"><FileIcon size={14} className="shrink-0 text-slate-400" aria-hidden="true" /><span className="truncate">{file.name}</span></span>
              ) : (
                <span className="flex-1 text-[13px] text-slate-600">Drop the receipt here, or choose a file.</span>
              )}
              {file && <button type="button" onClick={() => setFile(null)} className="btn-ghost btn-sm">Remove</button>}
              <button id={id} type="button" onClick={() => fileRef.current?.click()} className="btn-secondary btn-sm"><Upload size={13} aria-hidden="true" />{file ? 'Replace' : 'Choose file'}</button>
            </div>
          )}
        </Field>
      </form>
    </Dialog>
  );
}

/* ── Page ─────────────────────────────────────────────────────────────── */

export default function Reimbursements() {
  const { user, isAdmin, isOffice } = useAuth();
  const confirm = useConfirm();
  const canReview = isAdmin || isOffice;
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [staff, setStaff] = useState([]);
  const [status, setStatus] = useUrlTab('status', 'pending', ['all', 'pending', 'approved', 'reimbursed', 'rejected']);
  const [search, setSearch] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [rejecting, setRejecting] = useState(null);   // array of claims
  const [busy, setBusy] = useState(false);

  // All claims are loaded once: the segment counts and the money totals are the whole picture.
  const load = useCallback(() => {
    setLoading(true); setLoadError(null);
    api.get('/reimbursements')
      .then((r) => setClaims(Array.isArray(r.data) ? r.data : []))
      .catch((e) => setLoadError(e.response?.data?.message || "We couldn't load claims. Check your connection, then try again."))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (canReview) api.get('/staff').then((r) => setStaff(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, [canReview]);
  useEffect(() => { setSelected([]); }, [status]);
  const closeViewer = useStableCallback(() => setViewing(null));

  const by = useMemo(() => {
    const g = { pending: [], approved: [], reimbursed: [], rejected: [] };
    claims.forEach((c) => { (g[c.status] || (g[c.status] = [])).push(c); });
    return g;
  }, [claims]);
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return claims.filter((c) => (status === 'all' || c.status === status)
      && (!q || `${c.staff_name || ''} ${c.category || ''} ${c.description || ''} ${c.client_name || ''}`.toLowerCase().includes(q)));
  }, [claims, status, search]);
  const selectedClaims = useMemo(() => claims.filter((c) => selected.includes(c.id)), [claims, selected]);
  const selPending = selectedClaims.filter((c) => c.status === 'pending');
  const selApproved = selectedClaims.filter((c) => c.status === 'approved');

  // Runs one status change per claim and reports exactly what happened.
  const runEach = async (list, action, note) => {
    setBusy(true);
    let ok = 0; const failed = [];
    for (const c of list) {
      try { await api.put(`/reimbursements/${c.id}/${action}`, note ? { note } : {}); ok += 1; }
      catch (e) { failed.push(`${c.staff_name}: ${e.response?.data?.message || 'failed'}`); }
    }
    setBusy(false);
    const verb = action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'marked paid';
    if (ok) toast.success(`${plural(ok, 'claim')} ${verb}`);
    if (failed.length) toast.error(`${plural(failed.length, 'claim')} not ${verb}. ${failed[0]}${failed.length > 1 ? ' …' : ''}`, { duration: 6000 });
    setSelected([]);
    load();
    return !failed.length;
  };

  const approveClaims = async (list) => {
    if (!list.length) return;
    const total = money(sumMoney(list));
    const ok = await confirm(
      list.length === 1
        ? `${list[0].staff_name}, ${list[0].category}, ${dateText(list[0].expense_date)}. It moves to Approved, ready to be paid.`
        : `${plural(list.length, 'claim')} across ${plural(new Set(list.map((c) => c.staff_id)).size, 'staff member')} move to Approved, ready to be paid.`,
      { title: list.length === 1 ? `Approve ${total}?` : `Approve ${plural(list.length, 'claim')}, ${total}?`, confirmLabel: list.length === 1 ? 'Approve' : `Approve ${list.length}` },
    );
    if (ok) { await runEach(list, 'approve'); setViewing(null); }
  };
  const markPaid = async (list) => {
    if (!list.length) return;
    const total = money(sumMoney(list));
    const ok = await confirm(
      `Only do this once the money has been paid to ${list.length === 1 ? list[0].staff_name : 'these staff'}. ${plural(list.length, 'claim')} move to Reimbursed.`,
      { title: list.length === 1 ? `Mark ${total} as paid?` : `Mark ${plural(list.length, 'claim')} as paid, ${total}?`, confirmLabel: list.length === 1 ? 'Mark paid' : `Mark ${list.length} paid` },
    );
    if (ok) { await runEach(list, 'reimburse'); setViewing(null); }
  };
  const doReject = async (reason) => {
    const done = await runEach(rejecting, 'reject', reason);
    if (done) { setRejecting(null); setViewing(null); }
  };

  const remove = async (c) => {
    const msg = c.status !== 'pending'
      ? `This claim is ${(STATUS_VIEW[c.status] || {}).label || c.status}. Deleting it permanently removes its record and receipt, including from reports.`
      : `${c.staff_name}'s ${money(c.amount)} claim and its receipt will be deleted.`;
    if (!await confirm(msg, { title: 'Delete claim?', confirmLabel: 'Delete', danger: true })) return;
    try { await api.delete(`/reimbursements/${c.id}`); toast.success('Claim deleted'); setViewing(null); load(); }
    catch (e) { toast.error(e.response?.data?.message || "The claim wasn't deleted. Try again."); }
  };

  const exportCsv = (list, label) => {
    if (!list.length) { toast.error('Nothing to export'); return; }
    const esc = (v) => { const s = v == null ? '' : String(v).replace(/"/g, '""'); return /[",\n]/.test(s) ? `"${s}"` : s; };
    const origin = window.location.origin;
    const head = ['Staff', 'Expense date', 'Category', 'Amount', 'Status', 'Description', 'Client', 'Reviewed by', 'Review note', 'Receipt'];
    const rows = list.map((c) => [
      c.staff_name, String(c.expense_date || '').slice(0, 10), c.category, (cents(c.amount) / 100).toFixed(2),
      (STATUS_VIEW[c.status] || {}).label || c.status, c.description, c.client_name, c.reviewed_by_name, c.review_note,
      c.receipt_url ? `${origin}${c.receipt_url}` : '',
    ].map(esc).join(','));
    const csv = [head.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `expense-claims-${label}-${todayKey()}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    toast.success(`Exported ${plural(list.length, 'claim')}, ${money(sumMoney(list))}`);
  };

  const canDeleteClaim = (c) => canReview || (c.status === 'pending' && c.staff_id === user?.id);

  const columns = [
    { key: 'staff', header: 'Staff', sortable: true, sortValue: (c) => c.staff_name || '', render: (c) => (
      <Link to={`/staff/${c.staff_id}`} onClick={(e) => e.stopPropagation()} className="whitespace-nowrap font-medium text-slate-900 hover:text-primary-700 hover:underline">{c.staff_name}</Link>
    ) },
    { key: 'category', header: 'Category', sortable: true, render: (c) => <span className="whitespace-nowrap">{c.category}</span> },
    { key: 'expense_date', header: 'Date', sortable: true, defaultDir: 'desc', sortValue: (c) => String(c.expense_date || '').slice(0, 10), render: (c) => <span className="whitespace-nowrap tabular-nums">{dateText(c.expense_date)}</span> },
    // Claims made from a shift in the app carry the client and shift.
    { key: 'shift', header: 'Client / shift', render: (c) => (c.client_name || c.shift_date ? (
      <div className="text-[12.5px]">
        <p className="font-medium text-slate-700">{c.client_name || '—'}</p>
        {c.shift_date && <p className="text-slate-500">Shift {dateText(c.shift_date)}{c.shift_start ? ` · ${c.shift_start}` : ''}</p>}
      </div>
    ) : <span className="text-slate-400">—</span>) },
    { key: 'amount', header: 'Amount', money: true, sortable: true, sortValue: (c) => cents(c.amount) },
    { key: 'description', header: 'Description', className: 'max-w-[200px]', render: (c) => (c.description ? <span className="block truncate text-slate-600" title={c.description}>{c.description}</span> : <span className="text-slate-400">—</span>) },
    { key: 'receipt', header: 'Receipt', render: (c) => (c.receipt_url
      ? <button type="button" onClick={(e) => { e.stopPropagation(); setViewing(c); }} className="inline-flex items-center gap-1 text-[13px] font-medium text-primary-700 hover:underline"><Receipt size={13} aria-hidden="true" />View</button>
      : <span className="text-[12.5px] text-slate-500">None</span>) },
    { key: 'status', header: 'Status', render: (c) => (
      <div><ClaimStatus status={c.status} />{c.review_note && <p className="mt-1 max-w-[160px] truncate text-[12px] text-slate-500" title={c.review_note}>{c.review_note}</p>}</div>
    ) },
    { key: 'actions', header: <span className="sr-only">Actions</span>, align: 'right', render: (c) => (
      <span className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
        {canReview && c.status === 'pending' && (
          <>
            <button type="button" onClick={() => approveClaims([c])} className="btn-primary btn-sm" aria-label={`Approve ${c.staff_name}'s claim`}><Check size={13} aria-hidden="true" />Approve</button>
            <button type="button" onClick={() => setRejecting([c])} className="btn-secondary btn-sm" aria-label={`Reject ${c.staff_name}'s claim`}><XIcon size={13} aria-hidden="true" />Reject</button>
          </>
        )}
        {canReview && c.status === 'approved' && (
          <button type="button" onClick={() => markPaid([c])} className="btn-secondary btn-sm" aria-label={`Mark ${c.staff_name}'s claim as paid`}><BadgeCheck size={13} aria-hidden="true" />Mark paid</button>
        )}
        <OverflowMenu label={`More actions for ${c.staff_name}'s claim`} items={[
          { label: 'View claim', icon: Eye, onClick: () => setViewing(c) },
          canDeleteClaim(c) && { divider: true },
          canDeleteClaim(c) && { label: 'Delete claim', icon: Trash2, danger: true, onClick: () => remove(c) },
        ].filter(Boolean)} />
      </span>
    ) },
  ];

  const tabs = [
    { id: 'pending', label: 'Pending', count: by.pending.length },
    { id: 'approved', label: 'Approved', count: by.approved.length },
    { id: 'reimbursed', label: 'Reimbursed', count: by.reimbursed.length },
    { id: 'rejected', label: 'Rejected', count: by.rejected.length },
    { id: 'all', label: 'All', count: claims.length },
  ];

  return (
    <div className={PAGE_WIDE}>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-3 lg:flex-row lg:flex-wrap lg:items-center">
          <div className="max-w-full overflow-x-auto"><Tabs variant="segmented" size="sm" ariaLabel="Claim status" tabs={tabs} value={status} onChange={setStatus} /></div>
          <SearchInput value={search} onChange={setSearch} placeholder="Search staff, category or client" className="w-full lg:w-64" />
          {!loading && !loadError && (
            <dl className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[13px]">
              <div><dt className="inline text-slate-500">Pending </dt><dd className="inline font-semibold tabular-nums text-slate-900">{money(sumMoney(by.pending))}</dd> <span className="text-slate-500">({num(by.pending.length)})</span></div>
              <div><dt className="inline text-slate-500">Approved, not paid </dt><dd className="inline font-semibold tabular-nums text-slate-900">{money(sumMoney(by.approved))}</dd> <span className="text-slate-500">({num(by.approved.length)})</span></div>
            </dl>
          )}
          <div className="flex items-center gap-2 lg:ml-auto">
            {canReview && (
              <OverflowMenu label="Export claims" icon={() => <span className="inline-flex items-center gap-1.5"><Download size={14} aria-hidden="true" />Export</span>} className="btn-secondary btn-sm !text-slate-700" items={[
                { label: `CSV for payroll — approved, not paid (${num(by.approved.length)})`, icon: Download, onClick: () => exportCsv(by.approved, 'approved'), disabled: !by.approved.length },
                { label: `CSV — this view (${num(visible.length)})`, icon: Download, onClick: () => exportCsv(visible, status), disabled: !visible.length },
              ]} />
            )}
            <button type="button" onClick={() => setNewOpen(true)} className="btn-primary btn-sm"><Plus size={14} aria-hidden="true" />New claim</button>
          </div>
        </div>
        <DataTable
          label="Expense claims"
          columns={columns}
          rows={visible}
          loading={loading}
          error={loadError}
          onRetry={load}
          defaultSort={{ key: 'expense_date', dir: 'desc' }}
          selectable={canReview}
          selected={selected}
          onSelectedChange={setSelected}
          onRowClick={(c) => setViewing(c)}
          footer={visible.length > 1 ? <span className="tabular-nums">{plural(visible.length, 'claim')} · {money(sumMoney(visible))}</span> : null}
          empty={claims.length === 0
            ? { icon: Receipt, title: 'No expense claims yet', hint: 'Staff claim expenses from the mobile app, with a photo of the receipt. You can also add one here.', action: <button type="button" onClick={() => setNewOpen(true)} className="btn-primary btn-sm"><Plus size={14} aria-hidden="true" />New claim</button> }
            : { icon: Receipt, title: status === 'pending' && !search ? 'Nothing waiting for review' : 'No claims match', hint: search ? 'Try another search.' : 'Claims with other statuses are under the other tabs.', action: <button type="button" onClick={() => { setSearch(''); setStatus('all'); }} className="btn-secondary btn-sm">Show all claims</button> }}
        />
      </div>

      {canReview && (
        <BulkBar count={selected.length} noun={selected.length === 1 ? 'claim selected' : 'claims selected'} onClear={() => setSelected([])}>
          <span className="text-[12.5px] tabular-nums text-slate-600">{money(sumMoney(selectedClaims))}</span>
          {selPending.length > 0 && <button type="button" disabled={busy} onClick={() => approveClaims(selPending)} className="btn-primary btn-sm"><Check size={13} aria-hidden="true" />Approve ({selPending.length})</button>}
          {selPending.length > 0 && <button type="button" disabled={busy} onClick={() => setRejecting(selPending)} className="btn-secondary btn-sm"><XIcon size={13} aria-hidden="true" />Reject ({selPending.length})</button>}
          {selApproved.length > 0 && <button type="button" disabled={busy} onClick={() => markPaid(selApproved)} className="btn-secondary btn-sm"><BadgeCheck size={13} aria-hidden="true" />Mark paid ({selApproved.length})</button>}
          <button type="button" onClick={() => exportCsv(selectedClaims, 'selected')} className="btn-secondary btn-sm"><Download size={13} aria-hidden="true" />Export CSV</button>
        </BulkBar>
      )}

      {/* Claim and receipt, without leaving the list */}
      <DetailDrawer
        open={!!viewing}
        onClose={closeViewer}
        width="lg"
        title={viewing ? `${viewing.staff_name} · ${money(viewing.amount)}` : ''}
        subtitle={viewing ? `${viewing.category}, ${dateText(viewing.expense_date)}` : ''}
        statusNode={viewing ? <ClaimStatus status={viewing.status} /> : null}
        meta={viewing ? [
          { label: 'Amount', value: money(viewing.amount), money: true },
          { label: 'Client', value: viewing.client_name },
          viewing.shift_date && { label: 'Shift', value: `${dateText(viewing.shift_date)}${viewing.shift_start ? ` · ${viewing.shift_start}` : ''}` },
          viewing.reviewed_by_name && { label: 'Reviewed by', value: viewing.reviewed_by_name },
        ].filter(Boolean) : []}
        footer={viewing && (
          <>
            {canDeleteClaim(viewing) && <button type="button" onClick={() => remove(viewing)} className="btn-ghost btn-sm mr-auto !text-rose-700"><Trash2 size={13} aria-hidden="true" />Delete</button>}
            {canReview && viewing.status === 'pending' && (
              <>
                <button type="button" onClick={() => setRejecting([viewing])} className="btn-secondary btn-sm"><XIcon size={13} aria-hidden="true" />Reject</button>
                <button type="button" onClick={() => approveClaims([viewing])} className="btn-primary btn-sm"><Check size={13} aria-hidden="true" />Approve</button>
              </>
            )}
            {canReview && viewing.status === 'approved' && <button type="button" onClick={() => markPaid([viewing])} className="btn-primary btn-sm"><BadgeCheck size={13} aria-hidden="true" />Mark paid</button>}
          </>
        )}
      >
        {viewing && (
          <div className="space-y-5 text-sm">
            {viewing.description && <section><h4 className="mb-1 text-[12.5px] font-semibold text-slate-500">Description</h4><p className="whitespace-pre-wrap text-slate-800">{viewing.description}</p></section>}
            {viewing.review_note && <section><h4 className="mb-1 text-[12.5px] font-semibold text-slate-500">Review note</h4><p className="whitespace-pre-wrap text-slate-800">{viewing.review_note}</p></section>}
            <section>
              <div className="mb-2 flex items-center justify-between gap-2">
                <h4 className="text-[12.5px] font-semibold text-slate-500">Receipt</h4>
                {viewing.receipt_url && <a href={receiptHref(viewing.receipt_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[13px] font-medium text-primary-700 hover:underline">Open in a new tab<ExternalLink size={12} aria-hidden="true" /></a>}
              </div>
              {!viewing.receipt_url ? <p className="text-slate-600">No receipt was attached.</p>
                : isImage(viewing.receipt_url) ? <img src={receiptHref(viewing.receipt_url)} alt={`Receipt for ${viewing.staff_name}'s ${viewing.category} claim`} className="max-h-[60vh] w-full rounded-lg border border-slate-200 object-contain bg-slate-50" />
                  : isPdf(viewing.receipt_url) ? (
                    <object data={receiptHref(viewing.receipt_url)} type="application/pdf" className="h-[60vh] w-full rounded-lg border border-slate-200" aria-label="Receipt PDF">
                      <p className="p-3 text-slate-600">This browser can't show the PDF here. <a href={receiptHref(viewing.receipt_url)} target="_blank" rel="noreferrer" className="font-medium text-primary-700 hover:underline">Open it in a new tab</a>.</p>
                    </object>
                  ) : <p className="text-slate-600">This file type can't be previewed. Use "Open in a new tab".</p>}
            </section>
          </div>
        )}
      </DetailDrawer>

      <NewClaimDialog open={newOpen} staff={staff} onClose={() => setNewOpen(false)} onSaved={load} />
      <ReasonDialog
        open={!!rejecting}
        onClose={() => setRejecting(null)}
        onConfirm={doReject}
        busy={busy}
        danger
        required
        title={rejecting ? (rejecting.length === 1 ? `Reject ${money(rejecting[0].amount)} for ${rejecting[0].staff_name}?` : `Reject ${plural(rejecting.length, 'claim')}, ${money(sumMoney(rejecting))}?`) : ''}
        description="The reason is saved on the claim, where the staff member can see it."
        label="Reason"
        placeholder="e.g. No receipt, or not a work expense"
        confirmLabel={rejecting && rejecting.length > 1 ? `Reject ${rejecting.length}` : 'Reject'}
      />
    </div>
  );
}
